import React, { useState, useEffect } from 'react';
import { Layout, Menu, Watermark, message, Button, Typography } from 'antd';
import { 
  TrophyOutlined, 
  CrownOutlined,
  HeartOutlined, 
  CarOutlined, 
  SearchOutlined, 
  BarChartOutlined,
  SettingOutlined,
  CalculatorOutlined,
  QuestionCircleOutlined,
  FunctionOutlined,
  StarOutlined,
  ThunderboltOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import PowerRankings from './components/PowerRankings';
import HeroRankings from './components/HeroRankings';
import PetRankings from './components/PetRankings';
import RideRankings from './components/RideRankings';
import SearchPage from './components/SearchPage';
import StatisticsPage from './components/StatisticsPage';
import SettingsPanel from './components/SettingsPanel';
import SystemPowerDetails from './components/SystemPowerDetails';
import DamageSimulator from './components/DamageSimulator';
import { AppSettings } from './types';
import GodWarRankings from './components/GodWarRankings';
import LeagueRankings from './components/LeagueRankings';
import ActivityRankings from './components/ActivityRankings';
import './App.css';
import { announcementAPI, visitorAPI } from './services/api';
import NavigationGuide from './components/NavigationGuide';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

const App: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKey, setSelectedKey] = useState('power');
  const [settings, setSettings] = useState<AppSettings>({
    useUnits: false,
    pageSize: 50,
    serverId: null
  });
  const [announcement, setAnnouncement] = useState<string>('');
  const [visitors, setVisitors] = useState<{online: number; total: number}>({online: 0, total: 0});

  // 保存设置到 localStorage
    useEffect(() => {
      const savedSettings = localStorage.getItem('ranking-dashboard-settings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings) as AppSettings;
          setSettings(parsed);
          if (parsed?.defaultPage) {
            setSelectedKey(parsed.defaultPage);
          }
        } catch (error) {
          console.error('加载设置失败:', error);
        }
      }
    }, []);
    // 手机端默认收起侧边栏
    useEffect(() => {
      if (window.innerWidth <= 768) {
        setCollapsed(true);
      }
    }, []);

  // 新增获取公告的副作用
  useEffect(() => {
    announcementAPI.getAnnouncement()
      .then(data => setAnnouncement(data.announcement))
      .catch(() => message.error('加载公告失败'));
    // 获取访问统计，使用本地唯一访客ID
    let vid = localStorage.getItem('visitor_id');
    if (!vid && window.crypto?.randomUUID) {
      vid = window.crypto.randomUUID();
      localStorage.setItem('visitor_id', vid);
    }
    visitorAPI.getVisitors(vid || '')
      .then(data => setVisitors({online: data.online, total: data.total}))
      .catch(() => console.error('加载访问统计失败'));
  }, []);

  const saveSettings = (newSettings: AppSettings) => {
    setSettings(newSettings);
    localStorage.setItem('ranking-dashboard-settings', JSON.stringify(newSettings));
    message.success('设置已保存');
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    setSelectedKey(key);
  };

  // 取消弹窗公告，改为在“使用指南”页展示

  const menuItems = [
    {
      key: 'guide',
      icon: <QuestionCircleOutlined />,
      label: '使用指南',
    },
    {
      key: 'power',
      icon: <TrophyOutlined />,
      label: '战力排行榜',
    },
    {
      key: 'hero',
      icon: <CrownOutlined />,
      label: '角色排行榜',
    },
    {
      key: 'pet',
      icon: <HeartOutlined />,
      label: '宠物排行榜',
    },
    {
      key: 'ride',
      icon: <CarOutlined />,
      label: '坐骑排行榜',
    },
    {
      key: 'godwar',
      icon: <ThunderboltOutlined />,
      label: '神魔排行榜',
    },
    {
      key: 'activity',
      icon: <StarOutlined />,
      label: '活动排行榜',
    },
    {
      key: 'league',
      icon: <CrownOutlined />,
      label: '联盟排行榜',
    },
    {
      key: 'search',
      icon: <SearchOutlined />,
      label: '全局搜索',
    },
    {
      key: 'calculator',
      icon: <CalculatorOutlined />,
      label: '六维计算器',
    },
    {
      key: 'systempower',
      icon: <FunctionOutlined />,
      label: '角色各系统详情',
    },
    {
      key: 'statistics',
      icon: <BarChartOutlined />,
      label: '统计分析',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
  ];

  const renderContent = () => {
    switch (selectedKey) {
      case 'guide':
        return <NavigationGuide settings={settings} announcement={announcement} onNavigate={(k)=>setSelectedKey(k)} />;
      case 'power':
        return <PowerRankings settings={settings} />;
      case 'hero':
        return <HeroRankings settings={settings} />;
      case 'pet':
        return <PetRankings settings={settings} />;
      case 'ride':
        return <RideRankings settings={settings} />;
      case 'godwar':
        return <GodWarRankings settings={settings} />;
      case 'activity':
        return <ActivityRankings settings={settings} />;
      case 'league':
        return <LeagueRankings settings={settings} />;
      case 'search':
        return <SearchPage settings={settings} />;
      case 'calculator':
        return <DamageSimulator settings={settings} />;
      case 'systempower':
        return <SystemPowerDetails settings={settings} />;
      case 'statistics':
        return <StatisticsPage settings={settings} />;
      case 'settings':
        return <SettingsPanel settings={settings} onSave={saveSettings} />;
      default:
        return <PowerRankings settings={settings} />;
    }
  };

  return (
    <Watermark 
      content="数值研究所Q群513291473" 
      gap={[150, 100]} 
      style={{ height: '100vh' }}
      font={{
        color: 'rgba(0, 0, 0, 0.1)',
        fontSize: 16
      }}
    >
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          trigger={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '100%' }}>
              {collapsed ? (
                <MenuUnfoldOutlined style={{ fontSize: 16 }} />
              ) : (
                <><MenuFoldOutlined style={{ fontSize: 16 }} /><span style={{ marginLeft: 4, fontSize: 14 }}>收起</span></>
              )}
            </div>
          }
          theme="light"
          style={{
            boxShadow: '2px 0 8px 0 rgba(29, 35, 41, 0.05)',
          }}
        >
          <div style={{ 
            height: 32, 
            margin: 16, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: collapsed ? '18px' : '16px',
            fontWeight: 'bold',
            color: '#1890ff'
          }}>
            {collapsed ? '榜' : '排行榜'}
          </div>
          <Menu
            theme="light"
            defaultSelectedKeys={['power']}
            selectedKeys={[selectedKey]}
            mode="inline"
            items={menuItems}
            onClick={handleMenuClick}
          />
        </Sider>
        
        <Layout className="site-layout">
          <Header style={{ 
            padding: '0 24px', 
            background: '#fff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <h1 style={{ margin: 0, color: '#1890ff', fontSize: '24px' }}>
              游戏排行榜数据
            </h1>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text style={{ marginRight: 16, color: '#666' }}>在线: {visitors.online} 今日访问: {visitors.total}</Text>
              <Button 
                type="text" 
                icon={<QuestionCircleOutlined />} 
                onClick={() => setSelectedKey('guide')}
                style={{ color: '#666', fontSize: '16px' }}
              />
            </div>
          </Header>
          
          <Content style={{ 
            margin: '16px', 
            padding: '24px',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            overflow: 'auto',
            overflowY: 'auto'
          }}>
            {renderContent()}
          </Content>
        </Layout>
      </Layout>
      
    </Watermark>
  );
};

export default App;
